import { useState, useEffect } from 'react';
import api from '../../api';

export default function RecipeForm({ mouldId, recipe, onSaved, onCancel }) {
  const isNew = !recipe;
  const [materials, setMaterials] = useState([]);
  const [form, setForm] = useState({
    name:        recipe?.name        || '',
    description: recipe?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Reconstruct base materials list: prefer stored type:'base' components, fall back to legacy material_id
  const existingBaseComps = (recipe?.recipe_components || []).filter(c => c.type === 'base');
  const [bases, setBases] = useState(
    existingBaseComps.length > 0
      ? existingBaseComps.map(c => ({ material_id: String(c.material_id), percentage: String(c.percentage ?? '') }))
      : recipe?.material_id
        ? [{ material_id: String(recipe.material_id), percentage: '' }]
        : [{ material_id: '', percentage: '' }]
  );

  const [components, setComponents] = useState(
    (recipe?.recipe_components || [])
      .filter(c => c.type !== 'base')
      .map(c => ({
        material_id: c.material_id,
        type:        c.type,
        percentage:  c.percentage,
        notes:       c.notes || '',
      }))
  );

  useEffect(() => { api.get('/api/v1/materials').then(r => setMaterials(r.data)); }, []);

  const baseMaterialList = materials.filter(m => (m.type || 'material') === 'material');
  const masterbatches    = materials.filter(m => m.type === 'masterbatch');
  const additives        = materials.filter(m => m.type === 'additive');

  function updateBase(i, key, value) {
    setBases(b => b.map((entry, idx) => idx !== i ? entry : { ...entry, [key]: value }));
  }
  function removeBase(i) { setBases(b => b.filter((_, idx) => idx !== i)); }

  function addComponent(type) {
    setComponents(c => [...c, { material_id: '', type, percentage: '', notes: '' }]);
  }
  function updateComponent(i, key, value) {
    setComponents(c => c.map((comp, idx) => idx !== i ? comp : { ...comp, [key]: value }));
  }
  function removeComponent(i) {
    setComponents(c => c.filter((_, idx) => idx !== i));
  }

  function save() {
    if (!form.name) { setError('Recipe name is required'); return; }
    const validBases = bases.filter(b => b.material_id);
    if (validBases.length === 0) { setError('At least one base material is required'); return; }
    setSaving(true);

    const payload = {
      mould_id:    mouldId || null,
      material_id: parseInt(validBases[0].material_id),
      name:        form.name,
      description: form.description,
      components: [
        ...validBases.map(b => ({
          material_id: parseInt(b.material_id),
          type:        'base',
          percentage:  b.percentage !== '' ? parseFloat(b.percentage) : null,
        })),
        ...components
          .filter(c => c.material_id && c.percentage)
          .map(c => ({
            material_id: parseInt(c.material_id),
            type:        c.type,
            percentage:  parseFloat(c.percentage),
            notes:       c.notes,
          })),
      ],
    };

    const req = isNew
      ? api.post('/api/v1/recipes', payload)
      : api.put(`/api/v1/recipes/${recipe.id}`, payload);
    req.then(r => onSaved(r.data)).catch(err => {
      setError(err.response?.data?.error || 'Save failed');
      setSaving(false);
    });
  }

  return (
    <div>
      {error && <div style={s.error}>{error}</div>}

      <div style={s.field}>
        <label style={s.label}>Recipe name *</label>
        <input style={s.input} value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Black PC 2% MB" />
      </div>

      <div style={s.field}>
        <label style={s.label}>Description</label>
        <input style={s.input} value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </div>

      {/* Base materials */}
      <div style={{ marginBottom: '12px' }}>
        <div style={s.label}>Base materials *</div>
        <div style={{ marginTop: '8px' }}>
          {bases.map((bm, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={s.baseBadge}>BASE</span>
              <select
                style={{ ...s.input, flex: 3, minWidth: '140px' }}
                value={bm.material_id}
                onChange={e => updateBase(i, 'material_id', e.target.value)}
              >
                <option value="">— Select —</option>
                {baseMaterialList.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <input
                type="number" placeholder="%"
                style={{ ...s.input, width: '70px', flex: 'none' }}
                value={bm.percentage}
                onChange={e => updateBase(i, 'percentage', e.target.value)}
              />
              <span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>
              {bases.length > 1 && (
                <button style={s.removeBtn} onClick={() => removeBase(i)}>×</button>
              )}
            </div>
          ))}
          <button style={s.addBtn} onClick={() => setBases(b => [...b, { material_id: '', percentage: '' }])}>
            + Add base material
          </button>
        </div>
      </div>

      {/* Masterbatches & Additives */}
      <div style={{ marginTop: '4px' }}>
        <div style={s.label}>Masterbatches & Additives</div>
        <div style={{ marginTop: '8px' }}>
          {components.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ ...s.baseBadge, background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534' }}>
                {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
              </span>
              <select style={{ ...s.input, flex: 2, minWidth: '140px' }}
                value={c.material_id} onChange={e => updateComponent(i, 'material_id', e.target.value)}>
                <option value="">— Select —</option>
                {(c.type === 'masterbatch' ? masterbatches : additives).map(m =>
                  <option key={m.id} value={m.id}>{m.name}</option>
                )}
              </select>
              <input type="number" placeholder="%" style={{ ...s.input, width: '70px', flex: 'none' }}
                value={c.percentage} onChange={e => updateComponent(i, 'percentage', e.target.value)} />
              <span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>
              <button style={s.removeBtn} onClick={() => removeComponent(i)}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button style={s.addBtn} onClick={() => addComponent('masterbatch')}>+ Masterbatch</button>
            <button style={s.addBtn} onClick={() => addComponent('additive')}>+ Additive</button>
          </div>
        </div>
      </div>

      <div style={s.footer}>
        {onCancel && <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>}
        <button style={{ ...s.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
          {saving ? 'Saving...' : isNew ? 'Save Recipe' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

const s = {
  field:     { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  label:     { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' },
  input:     { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px', fontFamily: 'system-ui, sans-serif' },
  baseBadge: { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: '#0f766e', color: '#fff', flexShrink: 0 },
  addBtn:    { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', cursor: 'pointer' },
  removeBtn: { background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px' },
  footer:    { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #2a3040' },
  cancelBtn: { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '10px 16px', fontSize: '13px', cursor: 'pointer' },
  saveBtn:   { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  error:     { background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' },
};
